/**
 * Christmas drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_drawing_topics`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const christmasDrawingsPack: ToilePack = {
    slug: 'christmas-drawings',
    category: 'coloring',
    name: 'Christmas drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'tree', label: 'Christmas tree', subject: 'a Christmas tree with a star, baubles and wrapped gifts underneath'},
        {key: 'snowman', label: 'Snowman', subject: 'a snowman with a scarf and hat beside a small pine tree and falling snow'},
        {key: 'church', label: 'Church night', subject: 'a small church with a lit window under a starry sky, snow on the roof'},
    ],
    styles: [
        {key: 'pencil', label: 'Pencil sketch', style: 'a soft graphite pencil sketch with clear outlines and light hatching for shade', palette: 'Gray pencil on white paper, no color.', framing: 'Drawn on a plain white paper sheet, centered with a white margin.'},
        {key: 'crayon', label: 'Colour pencil', style: 'a colour pencil and crayon drawing, simple outlines filled with bright even strokes of colour', palette: 'Bright crayon colours on white paper.', framing: 'Drawn on a plain white drawing-book page, centered with a white margin.'},
        {key: 'outline', label: 'Outline to colour', style: 'a bold black outline drawing with flat marker colour filling each shape', palette: 'Black outlines with flat bright colour fills on white paper.', framing: 'Flat 2D drawing isolated on a pure white background with a white margin, clean closed black line work.'},
    ],
    directions: ['a gift box', 'a reindeer', 'a star'],
    directionHint: 'try: a gift box',
    seo: {
        title: 'Christmas Drawings: Tree, Snowman, Church at Night',
        metaDescription: 'Nine easy Christmas drawings: a tree with gifts, a snowman and a church under a starry sky, in pencil, colour and outline.',
        h1: 'Christmas drawings: Christmas tree, snowman, church at night',
        tagline: 'Three easy scenes, each in pencil sketch, colour pencil and outline to colour.',
        intro: 'Christmas drawings for school, a card or a craft. The set shows a Christmas tree with a star, baubles and wrapped gifts, a snowman with a scarf and hat in falling snow, and a small church with a lit window under a starry sky. Each comes in three styles: pencil, colour pencil and outline to colour. Type a change in the direction field, such as a gift box, a reindeer or a star.',
        useCases: [
            'an easy drawing for a school competition',
            'a Christmas card for family',
            'a classroom wall decoration',
            'a colouring page for kids',
        ],
        faq: [
            {q: 'Can I print these for a school competition?', a: 'Yes. Each picture is a plain drawing on a white background, so it prints on ordinary paper.'},
            {q: 'What is the difference between the three styles?', a: 'Pencil sketch is grey shading with no colour. Colour pencil is bright crayon colour. Outline to colour is a bold black line drawing with flat colour inside each shape, easy to fill in.'},
            {q: 'Can I use these on a card?', a: 'Yes. Print the picture and add your own greeting by hand or on a separate sheet, since the pictures carry no text.'},
            {q: 'Is the church a particular church?', a: 'No. It is a small generic church, not a building from a particular place.'},
        ],
    },
}
